/**
 * The bench standing still: every part's free-body diagram, solved together.
 *
 * ## Per PART, not per body
 *
 * Two bolted parts are one rigid object, and the rest of the engine treats them
 * as one body — which is right for the dynamics and wrong here. Merging them
 * hides the force in the bolt, and a reader looking at a weight slung under a
 * pulley is owed the number the bolt carries. So every part gets its own
 * diagram, and a bolt becomes what it physically is: a pair of equal and
 * opposite forces, with the rigid join written down as two equations about
 * position rather than assumed by fusing the parts.
 *
 * ## The system
 *
 *   UNKNOWNS   where each part that can move stands            2 each
 *              the force in each one-sided condition           1 each
 *              the force in each bolt                          2 each
 *
 *   EQUATIONS  ΣF = 0 on each part that can move               2 each
 *              each condition bears, or has room, never both   1 each
 *              each bolt holds its two ends together           2 each
 *
 * Every unknown arrives with its own equation, so the system is square by
 * construction and "determinate" is a question about RANK rather than about
 * counting. A part that is PINNED, or that is structure (a hook on the beam, a
 * table on the floor), contributes neither an unknown nor an equation: it
 * cannot move, and whatever holds it supplies the difference. Its reaction is
 * read off afterwards, from its own ΣF.
 *
 * ## Why positions and forces have to be found together
 *
 * A pulley hangs off to one side because there is tension in the rope, and
 * there is tension in the rope because it hangs off to one side. Solved in
 * sequence — forces at fixed positions, then nudge the positions — the first
 * step has a perfect answer available whenever the geometry cannot balance:
 * every tension zero, which satisfies `ΣF = 0` on a massless pulley exactly and
 * leaves the load holding its own weight. There is then nothing to nudge by.
 * One Newton solve over both has no such trap.
 *
 * ## What a CONDITION is
 *
 * A rope span, the floor, the ceiling, and two weights closing on each other
 * are one kind of thing, and `constraints.ts` already says so:
 *
 *     c(q) ≥ 0,   λ ≥ 0,   c · λ = 0
 *
 * `c` is the room left before the limit, `λ` the force that stops it going
 * further, and the third line says the force is zero unless the limit is
 * actually reached.
 *
 * Which conditions BEAR is therefore not an input. It is not guessed at and
 * then corrected, either — those three lines go into the system AS a row,
 *
 *     φ(λ, c) = λ + c − √(λ² + c²)     which is zero exactly when
 *                                      λ ≥ 0, c ≥ 0 and λ·c = 0
 *
 * so a rope that should be slack comes out with zero tension because that is
 * what the equation says, not because something decided beforehand to leave it
 * out. Every condition carries one unknown and one row whether it bears or
 * not, the system stays square, and there is nothing to iterate over but
 * Newton itself. (This is the Fischer–Burmeister function; `min(λ, c)` says
 * the same thing and has a corner where this only has one at the origin,
 * which matters when the Jacobian is measured rather than differentiated.)
 *
 * It is also what lets a weight on a SLACK rope be answered at all. Left out
 * of the system, such a rope gives that weight no equation, `ΣF = mg ≠ 0`
 * cannot close, and nothing in the arithmetic can move it — the bench reports
 * no equilibrium for a weight that plainly has one, a little lower down. With
 * the row present, Newton raises the tension, the row then pushes the geometry
 * until the rope is taut, and the weight lands where it hangs.
 */
import { minRunLength } from './constraints';
import { solveLinear } from './linalg';
import { defOf } from './registry';
import { Part, Vec2, World } from './types';
import {
    bondsOf, extentAlong, freedomOf, partById, portAt, ropesOf, runLength, Segment, segmentsOf, spanId,
    spansOf, supportOf, tensionNames, normalNames,
} from './world';
import { DOWN } from './vec';

/** A metre of rope out of place counts as much as this many newtons. */
const PER_METRE = 100;

/** Converged when nothing is further out than this — newtons, and metres × 100. */
const TIGHT = 1e-9;

/** A force this small is not a force. Ropes and floors may not pull the wrong
 *  way, and this is how much the arithmetic is allowed to be wrong by. */
const NIL = 1e-7;

export interface Unknown {
    id: string;
    /** What a reader calls it: `P₁`, `T₁ in the rope`, `bolt H₁–P₁`. */
    label: string;
    kind: 'position' | 'tension' | 'normal' | 'bolt' | 'reaction';
    value: number;
    unit: 'm' | 'N';
    /**
     * Which VECTOR this is a component of, and which component.
     *
     * A position is one unknown with two numbers in it, not two unknowns that
     * happen to be named alike — and a reader checking the arithmetic reads it
     * that way: "where is P₁" has one answer, `(2.900, 0.750)`. Listed as `x
     * of P₁` and `y of P₁` on separate rows it reads as two facts, and the
     * pairing is left for the reader to spot.
     *
     * The COUNT is unaffected and must be: each component is a scalar unknown
     * with its own equation, and that is what makes the system square.
     */
    group?: string;
    axis?: 'x' | 'y';
}

/**
 * Why the bench cannot stand where it is.
 *
 * `adrift` is the honest answer and the only one worth printing: these parts'
 * forces do not add to zero and no arrangement of the unknowns makes them.
 * `indeterminate` is the opposite complaint — many answers rather than none.
 */
export interface Fault {
    kind: 'no-equilibrium' | 'indeterminate';
    /** Parts whose ΣF will not close, and by how much. Real newtons. */
    adrift: { partId: string; name: string; leftover: Vec2 }[];
}

export interface Equilibrium {
    /** Where everything stands, once solved. */
    world: World;
    /** Every unknown, named, with what it came to. */
    unknowns: Unknown[];
    /** Condition id → the force it carries, for the rest of the engine. A span
     *  id is `rope:<ropeId>:<span>`, so this doubles as the tension table. */
    force: Record<string, number>;
    solved: boolean;
    /** Set when `solved` is false. Nothing else is worth reading then. */
    fault?: Fault;
    steps: number;
}

/** Can this part be moved by the arrangement, or is it held? */
function movable(w: World, p: Part): boolean {
    if (p.pinned) return false;
    /* A surface never moves while the bench is being set up, even one let
     * slide. This solve gives a block on a face one equation along the face
     * and reads the face's push off afterwards, so nothing here could push the
     * surface back — moved, it would be pushed by nothing. A sliding wedge
     * under a block is a machine that runs; play is where it recoils. */
    if (defOf(p).supports) return false;
    /* A part resting on a surface IS movable — along the surface. It used to
     * be treated as fixed ("its face decides"), which made every unpinned
     * block on a slope a wall as far as setting up was concerned: a rope from
     * it over a pulley carried nothing, a free block on the far slope never
     * pulled that rope taut, and lengthening the rope only ever made slack. */
    return freedomOf(w, { ...p, pinned: undefined }).axes.length > 0;
}

/**
 * Which ways a part may be moved BY THE SOLVE, and how many unknowns that is.
 *
 * Two coordinates for an ordinary part. "A weight travels straight down" is
 * the DYNAMICS' restriction and the setup solve deliberately does not honour
 * it: where a hanging thing sits sideways is an output here, not an input, and
 * that is the whole reason a pulley can be placed at all.
 *
 * But a TRACK is a real restriction. A runner is bolted to the beam and slides
 * along it; it cannot leave the beam, and the beam takes whatever it is pushed
 * with across the track. Given two free coordinates it had no equation for its
 * height at all, so a bench with a runner on it came back with no answer —
 * which is not the bench's fault, it is the solve having invented a freedom
 * the part does not have.
 *
 * So: a tracked part is parameterised by its own axes, one unknown each, and
 * gets one ΣF equation each — along the track. Square either way, and the
 * across-track reaction is read off afterwards like a pin's.
 */
function ways(w: World, p: Part): Vec2[] {
    /* A part resting on a face is on a track too: it may slide along the face
     * and the face holds it against leaving. One unknown, one ΣF equation
     * along the face — and the face's push across it is read off afterwards,
     * the same as a runner's beam. */
    const on = supportOf(w, p);
    if (on) return [on.axes[0]];
    const f = freedomOf(w, { ...p, pinned: undefined });
    return f.track && f.axes.length ? f.axes : [{ x: 1, y: 0 }, { x: 0, y: 1 }];
}

/**
 * One thing that may push but never pull the other way.
 *
 * `room` is how far it is from its limit — positive is clear, zero is bearing,
 * negative is a limit already broken. `push` is the force it puts on one part
 * per unit of its multiplier, which is what makes it a row in that part's ΣF.
 */
interface Condition {
    id: string;
    kind: 'rope' | 'contact';
    label: string;
    /** Parts it can act on at all — so ΣF need not ask every condition. */
    on: string[];
    room: (segs: Segment[], w: World) => number;
    push: (segs: Segment[], w: World, partId: string) => Vec2;
}

/**
 * Parts held in place by a bolt, through however many bolts it takes.
 *
 * "Structure" is anything that cannot move at all: the beam, a table, a hand
 * that holds. Bolt a sheave to a hook and the sheave is structure too, and so
 * is whatever is bolted to that.
 */
function boltedToStructure(w: World): Set<string> {
    const fixed = new Set(w.parts.filter(p => !movable(w, p)).map(p => p.id));
    for (let pass = 0; pass < w.parts.length; pass++) {
        const before = fixed.size;
        bondsOf(w).forEach(bd => {
            if (fixed.has(bd.hangs.partId)) fixed.add(bd.holds.partId);
            if (fixed.has(bd.holds.partId)) fixed.add(bd.hangs.partId);
        });
        if (fixed.size === before) break;
    }
    return fixed;
}

/**
 * How far each part could travel before its ropes stop it.
 *
 * The SLACK in them: a weight on a rope with 40 cm to spare can fall 40 cm and
 * no further, and a weight with no rope on it at all can fall until it lands
 * on something. That number is what decides whether a limit — a floor, a
 * ceiling, another part — belongs in a setup answer.
 *
 * Taking up slack is a bench settling; sliding a TAUT rope is the machine
 * running. The first is the arrangement the reader drew arriving at itself and
 * the second is what play is for, so only the first may reach a limit. Asked
 * of whole bolted groups, because a weight bolted under a wheel travels when
 * the wheel does and not otherwise.
 *
 * Reading it as "is a taut rope on it" instead was nearly right and failed on
 * the case that matters: shrink a wheel and its rope is momentarily slack by
 * the arc it no longer carries, at which point NOTHING is taut, every limit
 * opens, and an unbalanced Atwood solves by sliding a metre and a half until
 * one weight jams against the pulley. That is a real equilibrium of the model
 * and it is the end of a run, reached by travelling — which is exactly what a
 * setup answer may not do.
 */
function reach(w: World): Map<string, number> {
    // Bolted parts travel together, so they are stopped together.
    const group = new Map<string, Set<string>>(w.parts.map(p => [p.id, new Set([p.id])]));
    bondsOf(w).forEach(bd => {
        const a = group.get(bd.hangs.partId), b = group.get(bd.holds.partId);
        if (!a || !b || a === b) return;
        const both = new Set([...a, ...b]);
        both.forEach(id => group.set(id, both));
    });

    const out = new Map<string, number>(w.parts.map(p => [p.id, Infinity]));
    spansOf(w).forEach(sp => {
        const spare = Math.max(0, sp.was - sp.now);
        sp.segments.forEach(g => [g.a.partId, g.b.partId].forEach(id => {
            (group.get(id) ?? new Set([id])).forEach(x => {
                out.set(x, Math.min(out.get(x) ?? Infinity, spare));
            });
        }));
    });
    // A part bolted to structure travels nowhere at all.
    boltedToStructure(w).forEach(id => out.set(id, 0));
    return out;
}


/** Every one-sided condition on this bench, bearing or not. */
function conditions(w: World): Condition[] {
    const out: Condition[] = [];
    const named = new Map(ropesOf(w).map(r => [r.id, r.label ?? 'the rope']));
    const symbol = tensionNames(w);

    /* ── a span of rope: as long as it was laid out, never longer ────────
     *
     * The length is the SPAN's, not the rope's, because a span is the stretch
     * along which the tension cannot vary — see `spansOf`. Its runs are summed
     * at whatever world it is asked about, so this is the same measurement the
     * drawing and the constraint rows use and cannot drift from them. */
    spansOf(w).forEach(span => {
        const mine = (segs: Segment[]): Segment[] =>
            segs.filter(g => spanId(g.ropeId, g.span) === span.id);
        out.push({
            id: span.id,
            kind: 'rope',
            label: `${symbol.get(span.id) ?? 'T'} in ${named.get(span.ropeId) ?? 'the rope'}`,
            on: [...new Set(span.segments.flatMap(g => [g.a.partId, g.b.partId]))],
            /* `runLength`, not `g.length`: the arc over a wheel is rope. Adding
             * up the straight parts alone left every span short by its wraps,
             * and the solve made the difference up by dropping the load — a
             * weight on a fixed pulley sank by exactly π·r. */
            room: segs => span.was - mine(segs).reduce((t, g) => t + runLength(g), 0),
            push: (segs, _w, partId) => mine(segs).reduce((v, g) => ({
                // a rope pulls each end toward the other
                x: v.x + (g.a.partId === partId ? g.dir.x : 0) - (g.b.partId === partId ? g.dir.x : 0),
                y: v.y + (g.a.partId === partId ? g.dir.y : 0) - (g.b.partId === partId ? g.dir.y : 0),
            }), { x: 0, y: 0 } as Vec2),
        });
    });

    /* ── two parts on one run cannot pass through each other ─────────── */
    segmentsOf(w).forEach(seg => {
        const pa = partById(w, seg.a.partId), pb = partById(w, seg.b.partId);
        if (!pa || !pb) return;
        out.push({
            id: `sep:${seg.ropeId}:${seg.index}`,
            kind: 'contact',
            label: `${defOf(pa).name(pa as never, w)} against ${defOf(pb).name(pb as never, w)}`,
            on: [pa.id, pb.id],
            /* Measured at the world being ASKED about, not the one this
             * condition was built from. Reading the parts out of the closure
             * made the room a constant: its derivative with respect to every
             * position was zero, so two parts a metre apart were reported
             * shoving each other with tens of newtons and nothing in the
             * arithmetic could move to disagree. */
            room: (segs, at) => {
                const g = segs.find(x => x.ropeId === seg.ropeId && x.index === seg.index);
                const qa = partById(at, seg.a.partId), qb = partById(at, seg.b.partId);
                return g && qa && qb ? g.length - minRunLength(at, g, qa, qb) : 1;
            },
            // they are pushed APART along the run
            push: (segs, _w, partId) => {
                const g = segs.find(x => x.ropeId === seg.ropeId && x.index === seg.index);
                if (!g) return { x: 0, y: 0 };
                if (g.a.partId === partId) return { x: -g.dir.x, y: -g.dir.y };
                if (g.b.partId === partId) return { x: g.dir.x, y: g.dir.y };
                return { x: 0, y: 0 };
            },
        });
    });

    /* ── the room: the floor pushes up, the ceiling stops things rising ──
     *
     * Only for parts the room could actually catch. A part BOLTED to
     * structure — a sheave on a ceiling hook — has its position fixed by the
     * bolt, so a room limit on it is a second answer to a question already
     * answered, and the two then share the load in whatever proportion the
     * arithmetic lands on. It landed on 397,780 N of ceiling against 397,878 N
     * of bolt: every diagram closing, every number absurd, and no row in the
     * system able to object, because both answers are correct and there is
     * nothing to choose between them. A redundant row is not a small error —
     * it is the difference between one answer and a family of them. */
    const founded = boltedToStructure(w);
    /* And a part on a TRACK is held by its track, not by the room.
     *
     * A runner rides the beam, and the beam is at the ceiling — so the ceiling
     * row said it was already through its limit by its own half-height and
     * pushed back with seventeen thousand newtons. Its height is the track's
     * answer, and a room limit on it is a second answer to a settled question,
     * exactly as for a part bolted to structure. */
    const tracked = (p: Part): boolean => !!freedomOf(w, { ...p, pinned: undefined }).track;
    // Nor a part on a FACE: where it can go is the face's answer, and its ends
    // are the limits — below. At a wedge's foot the floor and the face's end
    // meet at one point, and both rows at once would be a family of answers.
    w.parts.filter(p => movable(w, p) && !founded.has(p.id) && !tracked(p) && !supportOf(w, p)).forEach(p => {
        const half = (at: World): number => {
            const q = partById(at, p.id);
            return q ? extentAlong(at, q, DOWN) : 0;
        };
        const y = (at: World): number => partById(at, p.id)?.at.y ?? 0;
        out.push({
            id: `floor:${p.id}`, kind: 'contact', label: 'the floor', on: [p.id],
            room: (_segs, at) => (y(at) - half(at)) - at.room.floor,
            push: () => ({ x: 0, y: 1 }),
        });
        out.push({
            id: `ceiling:${p.id}`, kind: 'contact', label: 'the ceiling', on: [p.id],
            room: (_segs, at) => at.room.ceiling - (y(at) + half(at)),
            push: () => ({ x: 0, y: -1 }),
        });
    });

    /* ── the ends of a face ──────────────────────────────────────────────
     * A block on a surface slides along it and stops at its ends, in setting
     * up exactly as in the run. Measured at the world being asked about, and
     * in the block's own frame, so a block lying along a slope stops when its
     * end — not its middle — reaches the end of the face. */
    w.parts.filter(p => movable(w, p) && !founded.has(p.id) && supportOf(w, p)).forEach(p => {
        ([1, -1] as const).forEach((way, i) => {
            out.push({
                id: `edge:${p.id}:${i}`, kind: 'contact', label: 'the end of the face', on: [p.id],
                room: (_segs, at) => {
                    const q = partById(at, p.id);
                    const sup = q && supportOf(at, q);
                    if (!q || !sup) return 1;
                    return sup.half - extentAlong(at, q, sup.axes[0]) - way * sup.along(q.at);
                },
                // It pushes back toward the middle of the face.
                push: (_segs, at) => {
                    const q = partById(at, p.id);
                    const sup = q && supportOf(at, q);
                    return sup ? { x: -way * sup.axes[0].x, y: -way * sup.axes[0].y } : { x: 0, y: 0 };
                },
            });
        });
    });

    return out;
}

export function equilibrium(w0: World): Equilibrium {
    const parts = w0.parts;
    const free = parts.filter(p => movable(w0, p));
    const bonds = bondsOf(w0);
    const all = conditions(w0);

    const name = (id: string): string => {
        const p = partById(w0, id);
        return p ? defOf(p).name(p as never, w0) : id;
    };

    /* Every condition is in the system, bearing or not — see the head of the
     * file. The only ones left out are those NOTHING can act on: a rope strung
     * between two hooks, a floor under a bolted-down table. Their force is not
     * zero and not unknown either, it is simply not a question this solve can
     * be asked, and giving them a row would leave the system asking Newton to
     * move coordinates that do not exist. */
    /* What may hold this bench up, and what would have to be REACHED first.
     *
     * A rope span always counts, slack or not: rope going taut is the bench
     * settling into the arrangement the reader drew. Taking up slack is what a
     * bench does when you let go of it.
     *
     * A floor, a ceiling, two parts meeting — those are somewhere a part has
     * to TRAVEL to, and whether travelling is allowed is the one question this
     * file has to answer carefully, because there are two quite different
     * things it could mean:
     *
     *   NOTHING IS HOLDING IT. A weight just put on the bench, joined to
     *   nothing. It falls, and where it lands is the answer — so the floor is
     *   live for it, and so is anything else it could come to rest against.
     *
     *   THE MACHINE IS RUNNING. Both weights of an unbalanced Atwood are held,
     *   by a taut rope, and the arrangement still cannot stand. It does end up
     *   with the heavy side on the floor, and saying so is a true fact about
     *   the END OF A RUN and the wrong answer to a question about a bench
     *   being set up. What the reader is owed is the other one: this does not
     *   stand where you have put it. Press play and watch.
     *
     * So a limit is live when it is already bearing, or when the part is held
     * by nothing at all. `unheld` is the same test the old `settle` made
     * before it would let anything drop — "is ANYTHING here held up by
     * nothing? Only then is this a drop" — which was the one piece of that
     * module that was right. */
    const settled = segmentsOf(w0);
    const travel = reach(w0);
    const reachable = (c: Condition): boolean => c.kind === 'rope'
        || c.on.some(id => c.room(settled, w0) <= (travel.get(id) ?? 0) + 1e-6);
    const live = all.filter(c => reachable(c) && c.on.some(id => free.some(p => p.id === id)));
    /* And a bolt between two things that cannot move carries a force nothing
     * can determine — both its equations are `0 = 0`. That is a real statement
     * about the bench (the beam is holding both ends and how they share it is
     * not a question the statics can answer) and it must not be smuggled in as
     * two unknowns with no equations. */
    const joints = bonds.filter(bd =>
        free.some(p => p.id === bd.hangs.partId || p.id === bd.holds.partId));
    const got = solveWith(w0, free, joints, live);

    const at = got.at;

    // ── read it back ────────────────────────────────────────────────────
    /* Read off the WORLD the solve produced, not off the unknown vector.
     *
     * A tracked part has one unknown and an ordinary one has two, so counting
     * in twos along `u` reads the wrong number the moment a runner is on the
     * bench. Where a part ended up is a question the solved world answers
     * directly, whatever it was parameterised by. */
    const unknowns: Unknown[] = [
        ...free.flatMap((p): Unknown[] => {
            const to = partById(at, p.id)?.at ?? p.at;
            const label = `where ${name(p.id)} stands`;
            return [
                { id: `x:${p.id}`, label, kind: 'position', value: to.x, unit: 'm', group: `at:${p.id}`, axis: 'x' },
                { id: `y:${p.id}`, label, kind: 'position', value: to.y, unit: 'm', group: `at:${p.id}`, axis: 'y' },
            ];
        }),
        ...live.map((c, k): Unknown => ({
            id: c.id,
            label: c.kind === 'rope' ? c.label : `${c.label} on ${name(c.on[0])}`,
            kind: c.kind === 'rope' ? 'tension' : 'normal',
            value: got.lambda[k], unit: 'N',
        })),
        ...joints.flatMap((bd, k): Unknown[] => {
            const label = `bolt ${name(bd.holds.partId)}–${name(bd.hangs.partId)}`;
            return [
                { id: `bolt:${bd.id}:x`, label, kind: 'bolt', value: got.bolt[k * 2], unit: 'N', group: `bolt:${bd.id}`, axis: 'x' },
                { id: `bolt:${bd.id}:y`, label, kind: 'bolt', value: got.bolt[k * 2 + 1], unit: 'N', group: `bolt:${bd.id}`, axis: 'y' },
            ];
        }),
    ];

    /* What is left on each part, in newtons a reader can check against the
     * diagram. Zero everywhere is the whole of "it stands". */
    const adrift = free.map(p => {
        /* Its own rows, however many it has — one for a runner, two for an
         * ordinary part — summed back into bench coordinates. */
        const axes = ways(w0, p);
        const base = got.base.get(p.id) ?? 0;
        const leftover = axes.reduce((v, ax, k) => (
            { x: v.x + ax.x * got.r[base + k], y: v.y + ax.y * got.r[base + k] }
        ), { x: 0, y: 0 } as Vec2);
        return { partId: p.id, name: name(p.id), leftover };
    }).filter(a => Math.hypot(a.leftover.x, a.leftover.y) > 1e-4);

    const solvedNow = got.ok && !adrift.length && got.norm < 1e-4;

    const force: Record<string, number> = {};
    /* A force only goes in the table when there IS one. `undefined` is a
     * stronger statement than zero and the readouts rely on it: a slack rope
     * is not carrying nothing, it is not carrying.
     *
     * And NOTHING goes in it when the bench has no answer. What a failed solve
     * holds is the closest it could come to an arrangement that does not
     * exist; publishing those as tensions would put figures on a bench which
     * has none, and a reader checking the diagram would find them adding up. */
    if (solvedNow) live.forEach((c, k) => { if (got.lambda[k] > NIL) force[c.id] = got.lambda[k]; });

    /* A failed solve's POSITIONS are wherever Newton happened to stop, and
     * that is not a place the bench is — showing it would move the reader's
     * parts to illustrate an arrangement that has no answer. So the world that
     * comes back is the one that went in, and the fault says why. */
    return {
        world: solvedNow ? at : w0,
        unknowns,
        force,
        solved: solvedNow,
        ...(solvedNow ? {} : {
            fault: {
                kind: got.ok ? 'no-equilibrium' : 'indeterminate',
                adrift,
            } as Fault,
        }),
        steps: got.steps,
    };
}

interface Solve {
    /** Positions, laid out two per movable part. */
    u: number[];
    /** One per condition, in the order given. Zero means it is not bearing. */
    lambda: number[];
    /** Two per bolt. */
    bolt: number[];
    at: World;
    /** Where each movable part's unknowns start in `u` and its rows in `r`. */
    base: Map<string, number>;
    /** ΣF left on each movable part, one per way it may travel — real newtons. */
    r: number[];
    norm: number;
    ok: boolean;
    steps: number;
}

/**
 * Newton over positions, condition forces and bolt forces at once.
 *
 * The residual is stacked in the order the unknowns are: ΣF for each movable
 * part, then one complementarity row per condition, then two per bolt saying
 * its ends are in the same place. Position rows are metres and force rows are
 * newtons, so the metres are multiplied by `PER_METRE` to make the two
 * comparable — a scaling, not a weighting: it cannot change which answer is
 * right, only how fast it is found.
 */
function solveWith(
    w0: World,
    free: Part[],
    bonds: ReturnType<typeof bondsOf>,
    live: Condition[],
): Solve {
    /* Where each part's unknowns start, and which ways they point. */
    const axesOf = new Map(free.map(p => [p.id, ways(w0, p)]));
    const baseOf = new Map<string, number>();
    let nPos = 0;
    free.forEach(p => { baseOf.set(p.id, nPos); nPos += (axesOf.get(p.id) as Vec2[]).length; });
    const nL = live.length;
    const nB = bonds.length * 2;
    const N = nPos + nL + nB;

    /* Where each part is ACROSS the ways it may travel, which the solve never
     * changes. Rebuilding a position from its unknowns alone threw that away:
     * harmless for a part with two unknowns (there is nothing across both) and
     * for a runner on a beam at y = 0, and wrong for a block on a face at any
     * other height — it would have been put back on a line through the origin. */
    const originOf = new Map(free.map(p => {
        const along = (axesOf.get(p.id) as Vec2[]).reduce((q, ax) => {
            const s = ax.x * p.at.x + ax.y * p.at.y;
            return { x: q.x + ax.x * s, y: q.y + ax.y * s };
        }, { x: 0, y: 0 } as Vec2);
        return [p.id, { x: p.at.x - along.x, y: p.at.y - along.y }];
    }));
    const place = (u: number[]): World => ({
        ...w0,
        parts: w0.parts.map(p => {
            const axes = axesOf.get(p.id);
            if (!axes) return p;
            const base = baseOf.get(p.id) as number;
            const at = axes.reduce((q, ax, k) => ({ x: q.x + ax.x * u[base + k], y: q.y + ax.y * u[base + k] }),
                originOf.get(p.id) as Vec2);
            return { ...p, at };
        }),
    });

    const residual = (u: number[]): { r: number[]; at: World } => {
        const at = place(u);
        const segs = segmentsOf(at);
        const r: number[] = [];

        free.forEach(p => {
            const q = at.parts[at.parts.findIndex(x => x.id === p.id)];
            let f: Vec2 = { x: 0, y: -defOf(q).mass(q as never) * at.gravity };
            (defOf(q).forces?.(q as never, at) ?? []).forEach(x => {
                f = { x: f.x + x.vector.x, y: f.y + x.vector.y };
            });
            live.forEach((c, k) => {
                if (!c.on.includes(p.id)) return;
                const d = c.push(segs, at, p.id);
                f = { x: f.x + u[nPos + k] * d.x, y: f.y + u[nPos + k] * d.y };
            });
            bonds.forEach((bd, k) => {
                const v = { x: u[nPos + nL + k * 2], y: u[nPos + nL + k * 2 + 1] };
                if (bd.hangs.partId === p.id) f = { x: f.x + v.x, y: f.y + v.y };
                if (bd.holds.partId === p.id) f = { x: f.x - v.x, y: f.y - v.y };
            });
            /* ΣF = 0 ALONG each way it may travel. For an ordinary part those
             * are x and y and this is the whole vector; for a runner it is the
             * one direction it can go, and the beam takes the rest. */
            const axes = axesOf.get(p.id) as Vec2[];
            const base = baseOf.get(p.id) as number;
            axes.forEach((ax, k) => { r[base + k] = f.x * ax.x + f.y * ax.y; });
        });

        /* Each condition bears OR has room to spare, and never both.
         *
         * φ(λ, c) = λ + c − √(λ² + c²) is zero exactly on that statement, so
         * this one row says all three of `λ ≥ 0`, `c ≥ 0` and `λ·c = 0`. The
         * room is in metres and the force in newtons, so the metres are scaled
         * to make the two comparable — with `PER_METRE` a centimetre of room
         * weighs the same as a newton of force in deciding which of the two is
         * the binding one. */
        live.forEach((c, k) => {
            const lam = u[nPos + k];
            const room = c.room(segs, at) * PER_METRE;
            r.push(lam + room - Math.hypot(lam, room));
        });

        /* Each bolt holds its two parts in the SAME RELATIVE POSITION.
         *
         * Not in the same place. A bolt is a bracket, and a bracket keeps
         * whatever gap it was made with — which is also what the rest of the
         * engine means by it: `bodiesOf` welds bonded parts into one body
         * carrying their offsets, and `movePart` drags them together. Written
         * as "the two ports coincide" instead, a sheave bolted to a ceiling
         * hook was pulled up INTO the beam, every preset moved on being solved,
         * and two versions of "bolted" disagreed about where a bench is. */
        bonds.forEach(bd => {
            const a = partById(at, bd.hangs.partId), b = partById(at, bd.holds.partId);
            const a0 = partById(w0, bd.hangs.partId), b0 = partById(w0, bd.holds.partId);
            if (!a || !b || !a0 || !b0) { r.push(0, 0); return; }
            r.push(
                ((a.at.x - b.at.x) - (a0.at.x - b0.at.x)) * PER_METRE,
                ((a.at.y - b.at.y) - (a0.at.y - b0.at.y)) * PER_METRE,
            );
        });
        return { r, at };
    };

    const norm = (r: number[]): number => Math.sqrt(r.reduce((s, v) => s + v * v, 0));

    /* Where to START, which is not the same as what to assume.
     *
     * Newton needs somewhere to begin and every unknown here is free to end up
     * anywhere; a starting point decides nothing. It does have to be a sane
     * one, for two reasons that pull opposite ways:
     *
     *   a condition with ROOM to spare wants λ = 0, and starting every one of
     *   them at the weight of the heaviest part meant a bench where the floor
     *   shoved up on a weight hanging a metre above it, and 120 steps spent
     *   unwinding forces that should never have been there;
     *
     *   a condition ON its limit wants λ ≠ 0, because `φ(0, 0) = 0` is the one
     *   place this row has a corner — it is satisfied there for any direction
     *   at all, and a measured Jacobian reads that as no information. Every
     *   taut rope on a bench as drawn starts exactly there.
     *
     * So: nothing for the ones with room, the heaviest weight for the ones
     * already bearing. Both are ordinary Newton starting points. */
    const heaviest = Math.max(1, ...w0.parts.map(p => defOf(p).mass(p as never) * w0.gravity));
    const segs0 = segmentsOf(w0);
    let u = [
        ...free.flatMap(p => (axesOf.get(p.id) as Vec2[]).map(ax => ax.x * p.at.x + ax.y * p.at.y)),
        ...live.map(c => (c.room(segs0, w0) * PER_METRE <= 1 ? heaviest : 0)),
        ...bonds.flatMap(() => [0, -heaviest]),
    ];
    let out = residual(u);
    let steps = 0;
    let ok = true;

    for (; steps < 120 && norm(out.r) > TIGHT; steps++) {
        const H = 1e-7;
        const cols: number[][] = [];
        for (let c = 0; c < N; c++) {
            const v = [...u]; v[c] += H;
            const probe = residual(v).r;
            cols.push(probe.map((x, i) => (x - out.r[i]) / H));
        }
        const rows = out.r.length;
        /* Least squares on a square system. Square by construction, and rank
         * deficient whenever the arrangement does not actually pin itself
         * down — a rope nothing loads, a part nothing holds — which is a real
         * answer about the bench and is reported rather than patched. */
        const A = Array.from({ length: N }, (_, i) =>
            Array.from({ length: N }, (_, j) => {
                let s = 0;
                for (let k = 0; k < rows; k++) s += cols[i][k] * cols[j][k];
                return s + (i === j ? 1e-10 : 0);
            }));
        const b = Array.from({ length: N }, (_, i) => {
            let s = 0;
            for (let k = 0; k < rows; k++) s -= cols[i][k] * out.r[k];
            return s;
        });
        const stepV = solveLinear(A, b);
        if (!stepV) { ok = false; break; }

        let k = 1, moved = false;
        for (let t = 0; t < 30 && !moved; t++, k /= 2) {
            const v = u.map((x, i) => x + stepV[i] * k);
            const next = residual(v);
            if (norm(next.r) < norm(out.r)) { u = v; out = next; moved = true; }
        }
        if (!moved) break;
    }

    return {
        u,
        lambda: u.slice(nPos, nPos + nL),
        bolt: u.slice(nPos + nL),
        at: out.at,
        base: baseOf,
        r: out.r.slice(0, nPos),
        norm: norm(out.r),
        ok,
        steps,
    };
}

/**
 * One PART's free-body diagram, straight out of the solved system.
 *
 * Not re-derived, not projected onto anything: every force here is either a
 * variable the solve found or the part's own weight, and the sum of them is the
 * residual the solver drove to zero. So the diagram a reader checks and the
 * arithmetic the bench did are the same object — which is the only way the
 * diagram is worth checking.
 *
 * A held part (pinned, or structure) gets its reaction as the closing force:
 * it never had an equation, so whatever the rest come to is what is holding it.
 */
export function diagramOf(w: World, eq: Equilibrium, partId: string): {
    name: string;
    mass: number;
    forces: {
        label: string;
        vector: Vec2;
        unknown?: boolean;
        /**
         * WHERE it acts, as an offset from the part's centre, in metres.
         *
         * A rope over a wheel pulls at the rim, not at the axle, and that
         * offset is not decoration: it is the whole reason the two tensions
         * either side have to be equal. Drawn through the middle, the diagram
         * shows a wheel with no torque on it and quietly loses the argument
         * it exists to make. Weight acts at the centre and says so with a
         * zero.
         */
        at: Vec2;
    }[];
    net: Vec2;
    held: boolean;
    /** False when some force on this part has no value yet, and the diagram
     *  therefore cannot be added up. */
    resolved: boolean;
    /** How a reader would describe it: its mass, and what it may do. */
    facts: string[];
    /** What the part IS — its kind, so the sketch can draw a wheel as a wheel
     *  and a wedge as a wedge. Every part drawn as the same blue square made
     *  the diagram say nothing about which part it was a diagram OF. */
    shape: string;
    /** Its half-size in metres, so the sketch can scale the offsets above
     *  against the body it draws them on. */
    reach: number;
} | null {
    const at = eq.world;
    const p = partById(at, partId);
    if (!p) return null;
    const nm = (id: string): string => {
        const q = partById(at, id);
        return q ? defOf(q).name(q as never, at) : id;
    };
    const forces: { label: string; vector: Vec2; unknown?: boolean; at: Vec2 }[] = [];
    /** Where a force acts, as an offset from the part's centre. */
    const off = (q: Vec2): Vec2 => ({ x: q.x - p.at.x, y: q.y - p.at.y });
    const MIDDLE: Vec2 = { x: 0, y: 0 };

    const m = defOf(p).mass(p as never);
    // Weight acts at the centre of mass, which for a point mass is the point.
    if (m > 0) forces.push({ label: 'mg', vector: { x: 0, y: -m * at.gravity }, at: MIDDLE });
    (defOf(p).forces?.(p as never, at) ?? []).forEach(f =>
        forces.push({ label: f.label, vector: f.vector, at: MIDDLE }));

    /* Every condition that touches this part goes in the diagram, INCLUDING
     * the ones whose force is not known.
     *
     * A rope tied to a part is pulling on it whether or not the bench has
     * worked out how hard. Leaving it out because the solve failed produced
     * the one diagram that must never be drawn: a weight with its `mg` and its
     * pin and nothing else, adding to exactly zero, on a bench where a rope
     * was plainly tied to it. It is not wrong by a little — it omits a force
     * and then presents the sum as though nothing were missing.
     *
     * When the bench IS solved, a condition absent from the table is carrying
     * nothing — a slack rope — and leaving it out is right. The difference
     * between "carrying nothing" and "not worked out" is the whole of this. */
    const segs = segmentsOf(at);
    const tension = tensionNames(at);
    let resolved = true;
    conditions(at).forEach(c => {
        if (!c.on.includes(partId)) return;
        const d = c.push(segs, at, partId);
        if (Math.abs(d.x) < 1e-12 && Math.abs(d.y) < 1e-12) return;
        const lam = eq.force[c.id];
        // Solved, and not in the table: this one is genuinely carrying nothing.
        if (lam === undefined && eq.solved) return;
        // A limit nothing has reached is not a force on anything.
        if (lam === undefined && c.kind === 'contact' && c.room(segs, at) > 1e-6) return;
        /* One force per RUN, not one per span.
         *
         * A span is a stretch of rope with one tension in it, and it can touch
         * a part more than once — a rope arrives at a wheel and leaves it, and
         * both pulls act on that wheel. Summed into a single arrow they are
         * not a free-body diagram of anything; and named from "the other part
         * in the span" the label is picked out of a list that may hold three
         * or four parts, which is how a weight came to be told its rope pulled
         * it toward a weight hanging BELOW it while the arrow pointed up at
         * the pulley. The far end of the run is the only thing a pull can
         * honestly be named after. */
        if (c.kind === 'rope') {
            const runs = segs.filter(g => spanId(g.ropeId, g.span) === c.id);
            runs.forEach(g => {
                const toward = g.a.partId === partId ? g.b.partId
                    : g.b.partId === partId ? g.a.partId : '';
                if (!toward) return;
                const way = g.a.partId === partId ? 1 : -1;
                const label = `${tension.get(c.id) ?? 'T'} toward ${nm(toward)}`;
                // AT THE TANGENT POINT — the rim, not the axle.
                const grip = off(g.a.partId === partId ? g.pa : g.pb);
                if (lam === undefined) {
                    resolved = false;
                    forces.push({ label, vector: { x: 0, y: 0 }, unknown: true, at: grip });
                    return;
                }
                forces.push({ label, vector: { x: lam * way * g.dir.x, y: lam * way * g.dir.y }, at: grip });
            });
            return;
        }
        if (lam === undefined) {
            resolved = false;
            forces.push({ label: c.label, vector: { x: 0, y: 0 }, unknown: true, at: MIDDLE });
            return;
        }
        forces.push({ label: c.label, vector: { x: lam * d.x, y: lam * d.y }, at: MIDDLE });
    });

    bondsOf(at).forEach(bd => {
        const on = bd.hangs.partId === partId ? 1 : bd.holds.partId === partId ? -1 : 0;
        if (!on) return;
        const far = nm(on > 0 ? bd.holds.partId : bd.hangs.partId);
        // Where the bracket is bolted on, which is a port and not the middle.
        const mine = on > 0 ? bd.hangs : bd.holds;
        const grip = off(portAt(at, mine)?.at ?? p.at);
        const bx = eq.unknowns.find(u => u.id === `bolt:${bd.id}:x`)?.value;
        const by = eq.unknowns.find(u => u.id === `bolt:${bd.id}:y`)?.value;
        if (bx === undefined || by === undefined || !eq.solved) {
            resolved = false;
            forces.push({ label: `bolt from ${far}`, vector: { x: 0, y: 0 }, unknown: true, at: grip });
            return;
        }
        forces.push({ label: `bolt from ${far}`, vector: { x: on * bx, y: on * by }, at: grip });
    });

    /* The face's push, on a part free to slide along it.
     *
     * The solve gives such a part ONE equation — along the face — so nothing in
     * the table is the force holding it against the face. It is what the rest
     * come to across the face, reversed: the same reading a pin's reaction is,
     * and the normal force a textbook asks for (mg cos θ, less whatever a rope
     * lifts off it). Left out, a block on a slope drew a diagram that did not
     * add up across the slope and never showed N at all. */
    const seat = movable(w, p) ? supportOf(at, p) : undefined;
    if (seat && p.support) {
        const n = seat.axes[1];
        const pre = forces.reduce((s, f) => ({ x: s.x + f.vector.x, y: s.y + f.vector.y }), { x: 0, y: 0 } as Vec2);
        const across = pre.x * n.x + pre.y * n.y;
        // The NORMAL force — named N, N₁, … as a textbook names it.
        const label = normalNames(at).get(p.id) ?? 'N';
        if (!resolved) forces.push({ label, vector: { x: 0, y: 0 }, unknown: true, at: MIDDLE });
        else if (Math.abs(across) > 1e-9) forces.push({ label, vector: { x: -across * n.x, y: -across * n.y }, at: MIDDLE });
    }
    const net = forces.reduce((s, f) => ({ x: s.x + f.vector.x, y: s.y + f.vector.y }), { x: 0, y: 0 } as Vec2);
    const held = !movable(w, p);
    /* A held part never had an equation, so what holds it is exactly what the
     * others come to — that is the definition of a reaction, not a guess.
     *
     * And only when the others are KNOWN. Closing the diagram out of a sum
     * that is missing a term is how a pinned weight came to be shown holding
     * its own weight against nothing, on a bench where a rope was tied to it. */
    if (held) {
        const label = p.pinned ? 'the pin' : 'what holds it';
        if (!resolved) forces.push({ label, vector: { x: 0, y: 0 }, unknown: true, at: MIDDLE });
        else if (Math.hypot(net.x, net.y) > 1e-9) forces.push({ label, vector: { x: -net.x, y: -net.y }, at: MIDDLE });
    }
    /* What the part IS, said as the facts a reader would list: how much it
     * weighs, and what it is or is not free to do. "on its own" used to head
     * this pane, which is the textbook's phrase for isolating a free body and
     * tells a reader nothing they can check. */
    const facts: string[] = [];
    facts.push(m > 0 ? `${+m.toFixed(2)} kg` : 'massless');
    if (p.pinned) facts.push('pinned — held where it is');
    else if (supportOf(at, p)) facts.push('resting on a surface, free along it');
    else if (!movable(at, p)) facts.push('fixed — part of the structure');
    const ways = freedomOf(at, { ...p, pinned: undefined });
    if (!p.pinned && ways.track) facts.push('runs on a track');

    return {
        name: defOf(p).name(p as never, at),
        mass: m,
        facts,
        forces,
        net: held && resolved ? { x: 0, y: 0 } : net,
        held,
        resolved,
        shape: p.kind,
        reach: Math.max(defOf(p).extent(p as never).x, defOf(p).extent(p as never).y),
    };
}
